/**
 * Token Manager - In-memory JWT token handling
 * Access tokens are stored in memory only (not localStorage) for XSS protection.
 * Refresh tokens are stored in httpOnly cookies (handled by browser automatically).
 */

class TokenManager {
  constructor() {
    this.accessToken = null;
    this.isRefreshing = false;
    this.refreshSubscribers = [];
  }

  /**
   * Store access token in memory and mark that a session exists
   */
  setToken(token) {
    this.accessToken = token;
    localStorage.setItem('hasSession', 'true');
  }

  /**
   * Get access token from memory
   */
  getToken() {
    return this.accessToken;
  }

  /**
   * Clear access token from memory and session flag
   */
  clearToken() {
    this.accessToken = null;
    localStorage.removeItem('hasSession');
  }

  /**
   * Check if browser has had a session before (worth attempting refresh)
   */
  hasSession() {
    return localStorage.getItem('hasSession') === 'true';
  }

  /**
   * Check if token is expired or will expire soon
   */
  isTokenExpired(token, bufferMinutes = 60) {
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      const bufferTime = bufferMinutes * 60;
      return payload.exp < (currentTime + bufferTime);
    } catch (error) {
      console.error('Error parsing token:', error);
      return true;
    }
  }

  /**
   * Get token expiry information
   */
  getTokenInfo(token) {
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = payload.exp - currentTime;

      return {
        expiresAt: payload.exp,
        currentTime,
        timeUntilExpiry,
        isExpired: timeUntilExpiry <= 0,
        expiresInMinutes: Math.floor(timeUntilExpiry / 60),
        expiresInHours: Math.floor(timeUntilExpiry / 3600)
      };
    } catch (error) {
      console.error('Error parsing token info:', error);
      return null;
    }
  }

  /**
   * Check if we should refresh the token
   */
  shouldRefreshToken() {
    if (!this.accessToken) return true;
    return this.isTokenExpired(this.accessToken, 60);
  }

  /**
   * Add subscriber for refresh token results
   */
  addRefreshSubscriber(callback) {
    this.refreshSubscribers.push(callback);
  }

  /**
   * Notify all subscribers of refresh result
   */
  onRefreshComplete(error, token) {
    this.refreshSubscribers.forEach(callback => callback(error, token));
    this.refreshSubscribers = [];
  }

  /**
   * Set refreshing state
   */
  setRefreshingState(isRefreshing) {
    this.isRefreshing = isRefreshing;
  }

  /**
   * Check if currently refreshing
   */
  isCurrentlyRefreshing() {
    return this.isRefreshing;
  }
}

// Export singleton instance
const tokenManager = new TokenManager();
export default tokenManager;
